import axios from 'axios'
import { API_BASE } from '../constants/inviteCode'

/**
 * API helpers for authority departments, their areas of responsibility (AoRs),
 * and the read-only default template sets those are cloned from.
 *
 * Reads are open to any authenticated user; writes require the caller to belong
 * to the authority (user.authority_id === authority_id) or be a superuser —
 * the server enforces this and returns 403 otherwise.
 */

const authConfig = (token, extra = {}) => ({
  headers: { Authorization: `Bearer ${token}` },
  ...extra,
})

const SLUG_RE = /^[a-z0-9_]+$/

/** Slugs are optional; when present they must match the server's rule. */
export const isValidSlug = (slug) => SLUG_RE.test(slug)

/**
 * Issue keywords are free-text labels attached to an AoR. We keep the display
 * text as the user entered it but compare/dedupe case-insensitively on a
 * normalized key (trimmed, collapsed whitespace, lowercased).
 */
export const normalizeKeyword = (value) =>
  String(value ?? '').trim().replace(/\s+/g, ' ')

export const keywordKey = (value) => normalizeKeyword(value).toLowerCase()

/** Dedupe a keyword list (case-insensitive), dropping blanks, preserving order. */
export const dedupeKeywords = (list) => {
  const seen = new Set()
  const out = []
  for (const raw of list ?? []) {
    const value = normalizeKeyword(raw)
    if (!value) continue
    const key = value.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push(value)
  }
  return out
}

/**
 * Build a create body from form state. `name` is required; slug/description are
 * only sent when non-empty so the server can derive the slug and keep bodies to
 * documented keys (unknown/empty keys can trip 422). `sort_order` is deliberately
 * omitted — ordering is managed through the reorder endpoints.
 */
export const buildCreateBody = (form) => {
  const body = {
    name: form.name.trim(),
    active: Boolean(form.active),
    is_catch_all: Boolean(form.is_catch_all),
  }
  const description = form.description?.trim()
  if (description) body.description = description
  const slug = form.slug?.trim()
  if (slug) body.slug = slug
  if (Array.isArray(form.keywords)) {
    const keywords = dedupeKeywords(form.keywords)
    if (keywords.length) body.keywords = keywords
  }
  return body
}

/** Partial update: only the fields that actually changed vs. the original row. */
export const buildUpdateBody = (form, original = {}) => {
  const body = {}

  const name = form.name.trim()
  if (name !== (original.name ?? '')) body.name = name

  const description = form.description?.trim() ?? ''
  if (description !== (original.description ?? '')) {
    body.description = description || null
  }

  if (Boolean(form.active) !== Boolean(original.active)) {
    body.active = Boolean(form.active)
  }

  if (Boolean(form.is_catch_all) !== Boolean(original.is_catch_all)) {
    body.is_catch_all = Boolean(form.is_catch_all)
  }

  const slug = form.slug?.trim() ?? ''
  if (slug && slug !== (original.slug ?? '')) body.slug = slug

  if (Array.isArray(form.keywords)) {
    const next = dedupeKeywords(form.keywords)
    const prev = dedupeKeywords(original.keywords)
    if (next.join('\n') !== prev.join('\n')) body.keywords = next
  }

  return body
}

// ── Departments ────────────────────────────────────────────────────────────

export const listDepartments = (token, authorityId, includeInactive = false) =>
  axios
    .get(
      `${API_BASE}/authorities/${authorityId}/departments`,
      authConfig(token, { params: { include_inactive: includeInactive } })
    )
    .then((res) => res.data)

export const createDepartment = (token, authorityId, body) =>
  axios
    .post(`${API_BASE}/authorities/${authorityId}/departments`, body, authConfig(token))
    .then((res) => res.data)

export const updateDepartment = (token, authorityId, departmentId, body) =>
  axios
    .put(
      `${API_BASE}/authorities/${authorityId}/departments/${departmentId}`,
      body,
      authConfig(token)
    )
    .then((res) => res.data)

export const deleteDepartment = (token, authorityId, departmentId) =>
  axios
    .delete(
      `${API_BASE}/authorities/${authorityId}/departments/${departmentId}`,
      authConfig(token)
    )
    .then((res) => res.data)

/** Send every department id of the authority (active AND inactive) exactly once. */
export const reorderDepartments = (token, authorityId, departmentIds) =>
  axios
    .put(
      `${API_BASE}/authorities/${authorityId}/departments/reorder`,
      { department_ids: departmentIds },
      authConfig(token)
    )
    .then((res) => res.data)

// ── Areas of responsibility ─────────────────────────────────────────────────

export const listAors = (token, authorityId, departmentId, includeInactive = false) =>
  axios
    .get(
      `${API_BASE}/authorities/${authorityId}/departments/${departmentId}/aors`,
      authConfig(token, { params: { include_inactive: includeInactive } })
    )
    .then((res) => res.data)

export const createAor = (token, authorityId, departmentId, body) =>
  axios
    .post(
      `${API_BASE}/authorities/${authorityId}/departments/${departmentId}/aors`,
      body,
      authConfig(token)
    )
    .then((res) => res.data)

export const updateAor = (token, authorityId, departmentId, aorId, body) =>
  axios
    .put(
      `${API_BASE}/authorities/${authorityId}/departments/${departmentId}/aors/${aorId}`,
      body,
      authConfig(token)
    )
    .then((res) => res.data)

export const deleteAor = (token, authorityId, departmentId, aorId) =>
  axios
    .delete(
      `${API_BASE}/authorities/${authorityId}/departments/${departmentId}/aors/${aorId}`,
      authConfig(token)
    )
    .then((res) => res.data)

/** Send every AoR id of the department exactly once, in the desired order. */
export const reorderAors = (token, authorityId, departmentId, aorIds) =>
  axios
    .put(
      `${API_BASE}/authorities/${authorityId}/departments/${departmentId}/aors/reorder`,
      { aor_ids: aorIds },
      authConfig(token)
    )
    .then((res) => res.data)

// ── Defaults / authority types (read-only reference data) ───────────────────

export const listAuthorityTypes = (token) =>
  axios
    .get(`${API_BASE}/authority-types`, authConfig(token))
    .then((res) => res.data)

export const listDefaultDepartments = (token, typeId) =>
  axios
    .get(`${API_BASE}/authority-types/${typeId}/default-departments`, authConfig(token))
    .then((res) => res.data)

// ── Known issue keywords (detectable-object catalog) ────────────────────────

/**
 * The catalog of "known" issue keywords used to power the AoR keyword search.
 * Each detectable object contributes its label plus every alias, flattened into
 * a deduped, alphabetically sorted list of display strings.
 */
export const listDetectableObjects = (token) =>
  axios
    .get(`${API_BASE}/detectable-objects`, authConfig(token))
    .then((res) => res.data)

export const flattenKnownKeywords = (detectableObjects) => {
  const raw = []
  for (const obj of Array.isArray(detectableObjects) ? detectableObjects : []) {
    if (obj?.label) raw.push(obj.label)
    for (const alias of Array.isArray(obj?.aliases) ? obj.aliases : []) {
      if (alias) raw.push(alias)
    }
  }
  return dedupeKeywords(raw).sort((a, b) => a.localeCompare(b))
}
